import React, { useState, useEffect } from "react";
import axiosInstance from "../../../helpers/axios";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import "./AiCollectionOptimizer.css";

/**
 * AI Collection Optimizer dropdown for CollectionPerformance page.
 * Shows recovery probability, AI strategy, and priority sort.
 * Positioned between bucket summary table and loan-level table.
 */
export default function AiCollectionOptimizer({ loanData, onSortChange }) {
  const decode = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortMode, setSortMode] = useState("dpd"); // dpd | amount | ai
  const [yesterdayStats, setYesterdayStats] = useState(null);

  const role = decode?.role;
  const hasNoAccess = ["institute_admin", "institute_user", "channel_user"].includes(role);

  useEffect(() => {
    if (hasNoAccess) return;
    fetchScores();
  }, []);

  const fetchScores = async () => {
    setLoading(true);
    try {
      const [scoresRes, yesterdayRes] = await Promise.all([
        axiosInstance.get("/bsi/portfolio/aggregates"),
        axiosInstance.get("/analytics/collection-performance/yesterday-ai-stats"),
      ]);
      setScores(scoresRes?.data || null);
      setYesterdayStats(yesterdayRes?.data?.data || yesterdayRes?.data || null);
    } catch (err) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (mode) => {
    setSortMode(mode);
    if (onSortChange) onSortChange(mode);
  };

  if (hasNoAccess) return null;

  // Compute stats from loan data if available
  const overdueCount = loanData?.length || 0;
  const highPriority = loanData?.filter(l => (l.recoveryScore || 0) > 70)?.length || 0;
  const recoverableEstimate = loanData?.reduce((sum, l) => {
    const prob = (l.recoveryScore || 50) / 100;
    return sum + (l.sumemiamount || 0) * prob;
  }, 0) || 0;
  const totalOverdueAmount = loanData?.reduce((sum, l) => sum + (l.sumemiamount || 0), 0) || 0;

  const formatCurrency = (amount) => "₹" + Math.round(amount).toLocaleString("en-IN");

  return (
    <div className={`ai-optimizer ${isOpen ? "ai-optimizer-open" : ""}`}>
      <div className="ai-optimizer-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="ai-optimizer-header-left">
          <h3 className="ai-optimizer-title">🎯 AI Collection Optimizer</h3>
          <span className="ai-optimizer-tag">AI Powered</span>
          {!isOpen && overdueCount > 0 && (
            <div className="ai-optimizer-quick-stats">
              <span className="ai-optimizer-quick-stat">Recoverable: {formatCurrency(recoverableEstimate)}</span>
              <span className="ai-optimizer-quick-stat">High Priority: {highPriority}</span>
            </div>
          )}
        </div>
        <div className="ai-optimizer-header-right">
          <span className="ai-optimizer-cron">Daily cron • Cached</span>
          <span className={`ai-optimizer-arrow ${isOpen ? "ai-optimizer-arrow-open" : ""}`}>▾</span>
        </div>
      </div>

      {isOpen && (
        <div className="ai-optimizer-body">
          <div className="ai-optimizer-stats-row">
            <div className="ai-optimizer-stat-card">
              <div className="ai-optimizer-stat-val">{formatCurrency(recoverableEstimate)}</div>
              <div className="ai-optimizer-stat-lbl">Today AI-Estimated</div>
            </div>
            <div className="ai-optimizer-stat-card">
              <div className="ai-optimizer-stat-val">{formatCurrency(yesterdayStats?.estimatedRecoverable || 0)}</div>
              <div className="ai-optimizer-stat-lbl">Yesterday AI-Estimated</div>
            </div>
            <div className="ai-optimizer-stat-card">
              <div className="ai-optimizer-stat-val">{formatCurrency(yesterdayStats?.actualCollected || 0)}</div>
              <div className="ai-optimizer-stat-lbl">Yesterday Actually Collected</div>
            </div>
            <div className="ai-optimizer-stat-card">
              <div className="ai-optimizer-stat-val" style={{ color: (yesterdayStats?.estimatedRecoverable > 0 ? ((yesterdayStats?.actualCollected / yesterdayStats?.estimatedRecoverable) * 100) : 0) >= 80 ? "#059669" : (yesterdayStats?.estimatedRecoverable > 0 ? ((yesterdayStats?.actualCollected / yesterdayStats?.estimatedRecoverable) * 100) : 0) >= 50 ? "#92400e" : "#d32028" }}>
                {yesterdayStats?.estimatedRecoverable > 0 ? ((yesterdayStats.actualCollected / yesterdayStats.estimatedRecoverable) * 100).toFixed(1) : "—"}%
              </div>
              <div className="ai-optimizer-stat-lbl">Yesterday AI Accuracy</div>
            </div>
            <div className="ai-optimizer-stat-card">
              <div className="ai-optimizer-stat-val">{highPriority}</div>
              <div className="ai-optimizer-stat-lbl">High-Priority Today</div>
            </div>
          </div>

          {/* Sort Toggle */}
          <div className="ai-optimizer-sort-row">
            <span style={{ fontSize: "11px", color: "#667085" }}>Sort loans by:</span>
            <div className="ai-optimizer-sort-toggle">
              <button className={`ai-optimizer-sort-btn ${sortMode === "dpd" ? "ai-optimizer-sort-active" : ""}`} onClick={(e) => { e.stopPropagation(); handleSortChange("dpd"); }}>DPD</button>
              <button className={`ai-optimizer-sort-btn ${sortMode === "amount" ? "ai-optimizer-sort-active" : ""}`} onClick={(e) => { e.stopPropagation(); handleSortChange("amount"); }}>Amount</button>
              <button className={`ai-optimizer-sort-btn ${sortMode === "ai" ? "ai-optimizer-sort-active" : ""}`} onClick={(e) => { e.stopPropagation(); handleSortChange("ai"); }}>🤖 AI Priority</button>
            </div>
          </div>

          <div className="ai-optimizer-footer-note">
            <div className="ai-optimizer-footer-dot"></div>
            Scores computed via daily cron (6 AM) • 0 credits per view • Stored in ai_collection_scores
          </div>
        </div>
      )}
    </div>
  );
}
